import React from "react";
import { Box, Typography, useTheme, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useReportQuota } from "../hooks/useReportQuota";

dayjs.extend(relativeTime);

interface QuotaIndicatorProps {
  compact?: boolean; // For mobile dropdown view
}

const QuotaIndicator: React.FC<QuotaIndicatorProps> = ({ compact = false }) => {
  const theme = useTheme();
  const { data: quota, isLoading } = useReportQuota();

  if (isLoading || !quota) {
    return null;
  }

  const { used, limit, remaining, resetDate } = quota;
  const isExhausted = remaining === 0;
  const isLow = remaining === 1;
  // Beyond the free quota, credits buy extra reports — the affordable count
  // comes straight from the API (floor(balance / report price)).
  const extraAvailable = quota.credits?.extraReportsAvailable ?? 0;

  // Calculate time until reset
  const resetTime = dayjs(resetDate);
  const timeUntilReset = resetTime.fromNow();

  // Determine indicator color
  const getIndicatorColor = (index: number) => {
    if (index < used) {
      return "#22c55e"; // Green for used
    }
    return theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.08)";
  };

  // Compact view for mobile - text only, no dots
  if (compact) {
    return (
      <Typography
        variant="caption"
        sx={{
          color: isExhausted
            ? "#ef4444"
            : isLow
              ? "#f59e0b"
              : theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.6)"
                : "rgba(0, 0, 0, 0.5)",
          fontSize: "0.75rem",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {used}/{limit}
      </Typography>
    );
  }

  // Refined footer view for sidebar
  return (
    <Tooltip
      title={
        isExhausted
          ? extraAvailable > 0
            ? `Weekly limit reached — ${extraAvailable} extra report${extraAvailable !== 1 ? "s" : ""} available with credits. Resets ${timeUntilReset}`
            : `Weekly limit reached. Resets ${timeUntilReset}`
          : `${remaining} report${remaining !== 1 ? "s" : ""} remaining. Resets ${timeUntilReset}`
      }
      arrow
      placement="top"
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderTop:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.06)"
              : "1px solid rgba(0, 0, 0, 0.06)",
          cursor: "default",
        }}
      >
        {/* Header label */}
        <Typography
          variant="caption"
          sx={{
            color:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.4)"
                : "rgba(0, 0, 0, 0.35)",
            fontSize: "0.6rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            display: "block",
            mb: 0.75,
          }}
        >
          Weekly Usage
        </Typography>

        {/* Progress bars and count */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {/* Progress bar indicators */}
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              flex: 1,
            }}
          >
            {Array.from({ length: limit }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: 3,
                  borderRadius: 0.5,
                  backgroundColor: getIndicatorColor(index),
                  transition: "background-color 0.2s ease",
                }}
              />
            ))}
          </Box>

          {/* Count */}
          <Typography
            variant="caption"
            sx={{
              color: isExhausted
                ? "#ef4444"
                : isLow
                  ? "#f59e0b"
                  : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.45)"
                    : "rgba(0, 0, 0, 0.4)",
              fontSize: "0.65rem",
              fontWeight: 500,
              minWidth: "28px",
              textAlign: "right",
            }}
          >
            {used}/{limit}
          </Typography>
        </Box>

        {/* Warning messages - only show when exhausted or low */}
        {isExhausted && extraAvailable > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: "#22c55e",
              fontSize: "0.6rem",
              display: "block",
              mt: 0.75,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            +{extraAvailable} extra with credits
          </Typography>
        )}
        {isExhausted && extraAvailable === 0 && (
          <Typography
            variant="caption"
            sx={{
              color: "#ef4444",
              fontSize: "0.6rem",
              display: "block",
              mt: 0.75,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            Limit reached
          </Typography>
        )}
        {!isExhausted && isLow && (
          <Typography
            variant="caption"
            sx={{
              color: "#f59e0b",
              fontSize: "0.6rem",
              display: "block",
              mt: 0.75,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            1 remaining
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default QuotaIndicator;
